const AMPLIFY_COOKIE_PREFIX = "CognitoIdentityServiceProvider.";

export const readAmplifyCookies = (prefix: string) =>
  document.cookie
    .split(";")
    .map((cookie) => {
      const trimmed = cookie.trim();
      const separator = trimmed.indexOf("=");
      return separator === -1
        ? { name: trimmed, value: "" }
        : {
            name: trimmed.slice(0, separator),
            value: trimmed.slice(separator + 1),
          };
    })
    .filter(({ name }) => name.startsWith(prefix));

const expireHostOnlyCookie = (name: string): void => {
  // biome-ignore lint/nursery/noDocumentCookie: the Cookie Store API is not available in every browser we support
  document.cookie = `${name}=; Max-Age=0; path=/`;
};

// beta.dev.medimo.ai → ["dev.medimo.ai", "medimo.ai"]. A single label (localhost, a TLD)
// cannot carry a Domain cookie, so it is never listed.
const parentDomainsOf = (hostname: string): string[] => {
  const labels = hostname.split(".");
  return labels
    .slice(1, -1)
    .map((_, index) => labels.slice(index + 1).join("."));
};

const expireOnParentDomains = (name: string, hostname: string): void => {
  for (const domain of parentDomainsOf(hostname)) {
    // biome-ignore lint/nursery/noDocumentCookie: the Cookie Store API is not available in every browser we support
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
  }
};

const currentHostname = (): string =>
  typeof window === "undefined" ? "" : window.location.hostname;

/**
 * The session is always host-only, both when Amplify writes it and when the backend hands
 * it over on the Collabo SSO callback. A copy on a parent domain can only have been planted by
 * a sibling subdomain (or left by an older release), and document.cookie would list it next to
 * ours with nothing to tell them apart, so Amplify could read the planted session.
 * Expires every Amplify cookie of this client on each parent domain of the current host;
 * host-only cookies are left alone. Runs before Amplify.configure and in every logout path.
 */
export const clearParentDomainAmplifyCookies = (
  clientId: string | undefined = process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
  hostname: string = currentHostname(),
): void => {
  if (!clientId) return;
  const prefix = `${AMPLIFY_COOKIE_PREFIX}${clientId}.`;
  for (const { name } of readAmplifyCookies(prefix)) {
    expireOnParentDomains(name, hostname);
  }
};

const consumeFlagCookie = (name: string, hostname: string): boolean => {
  const present = readAmplifyCookies(name).some(
    (cookie) => cookie.name === name,
  );
  if (!present) return false;
  expireHostOnlyCookie(name);
  expireOnParentDomains(name, hostname);
  return true;
};

const COLLABO_LINK_FAILED_COOKIE = "collabo_link_failed";

/**
 * The backend sets this flag when the user signed in but the Collabo registration failed,
 * so the link stays pending until the next Portal visit. Reads it once and drops it.
 */
export const consumeCollaboLinkFailedCookie = (
  hostname: string = currentHostname(),
): boolean => consumeFlagCookie(COLLABO_LINK_FAILED_COOKIE, hostname);

/**
 * Proves a `collabo_link` abort reason came from the link flow itself (the backend error
 * redirect or this SPA), so a crafted sign-in link cannot sign a user out.
 */
export const COLLABO_LINK_ABORT_COOKIE = "collabo_link_abort";

export const markCollaboLinkAbort = (): void => {
  // biome-ignore lint/nursery/noDocumentCookie: the Cookie Store API is not available in every browser we support
  document.cookie = `${COLLABO_LINK_ABORT_COOKIE}=1; Max-Age=60; path=/; SameSite=Lax`;
};

export const consumeCollaboLinkAbortCookie = (
  hostname: string = currentHostname(),
): boolean => consumeFlagCookie(COLLABO_LINK_ABORT_COOKIE, hostname);
